import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { api } from '@/lib/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { token, user } = await api.login(email, password);
      setAuth(token, user);
      navigate('/');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg,#0a1628 0%,#0d2044 50%,#0a1628 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden',
    }}>
      {/* Animated rings background */}
      {[320, 480, 640, 800].map((size, i) => (
        <div key={size} style={{
          position: 'absolute', width: size, height: size, borderRadius: '50%',
          border: `1px solid rgba(96,165,250,${0.08 - i * 0.015})`,
          top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          animation: `ringSpin ${18 + i * 6}s linear infinite`,
        }} />
      ))}
      {/* Color blobs */}
      <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle,rgba(29,78,216,0.18) 0%,transparent 70%)', top: '20%', left: '15%', animation: 'glowDrift 8s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle,rgba(96,165,250,0.12) 0%,transparent 70%)', bottom: '20%', right: '15%', animation: 'glowDrift 11s ease-in-out infinite reverse' }} />

      {/* Card */}
      <div style={{
        position: 'relative', zIndex: 10, width: '100%', maxWidth: 420, padding: '0 24px',
        animation: 'floatIn 500ms ease-out',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 64, height: 64, borderRadius: 20, background: 'rgba(29,78,216,0.2)', marginBottom: 16, backdropFilter: 'blur(12px)', border: '1px solid rgba(96,165,250,0.2)' }}>
            <svg width="28" height="28" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="27" stroke="#60A5FA" strokeWidth="3"/>
              <circle cx="24" cy="24" r="4" fill="#60A5FA"/>
              <circle cx="40" cy="36" r="3" fill="rgba(96,165,250,0.5)"/>
            </svg>
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.03em' }}>ComuniData</h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>Plataforma de gestión territorial</p>
        </div>

        {/* Form card */}
        <div style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', borderRadius: 24, padding: 32, border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 24px 48px rgba(0,0,0,0.3)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: '0 0 24px', textAlign: 'center' }}>Iniciar sesión</h2>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Correo electrónico</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="usuario@comunidata.com"
                style={{ width: '100%', padding: '11px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Contraseña</label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                placeholder="••••••••"
                style={{ width: '100%', padding: '11px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {error && (
              <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: 12 }}>
                {error}
              </div>
            )}

            <button
              type="submit" disabled={loading}
              style={{ marginTop: 8, padding: '13px', borderRadius: 14, background: loading ? 'rgba(29,78,216,0.5)' : 'linear-gradient(135deg,#1D4ED8,#2563EB)', color: '#fff', fontWeight: 700, fontSize: 14, letterSpacing: '0.01em', boxShadow: '0 8px 20px rgba(29,78,216,0.4)', cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'Ingresando…' : 'Ingresar'}
            </button>
          </form>

          {/* Demo credentials hint */}
          <div style={{ marginTop: 20, padding: '12px 14px', borderRadius: 12, background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.1)' }}>
            <p style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.35)', margin: 0, lineHeight: 1.8 }}>
              <strong style={{ color: 'rgba(96,165,250,0.6)' }}>Demo:</strong> admin@comunidata.com / admin123<br/>
              vialidad@comunidata.com / vial123 · brig@comunidata.com / brig123
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
